"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { isLocale } from "@/lib/i18n";
import styles from "./intro-overlay.module.css";

const OPEN_DURATION_MS = 1850;
const CLOSE_DURATION_MS = 720;
const REMOVE_DELAY_MS = 300;
const REDUCED_OPEN_DURATION_MS = 240;
const REDUCED_CLOSE_DURATION_MS = 180;
const REDUCED_REMOVE_DELAY_MS = 180;

function resolveLocaleFromPath(pathname: string | null) {
  if (!pathname) {
    return "fr";
  }

  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] === "wedding" && segments[1] && isLocale(segments[1])) {
    return segments[1];
  }

  return "fr";
}

function getIntroCopy(locale: string) {
  if (locale === "en") {
    return {
      hint: "Tap the envelope",
      ariaLabel: "Open invitation envelope",
    };
  }

  if (locale === "de") {
    return {
      hint: "Brief antippen",
      ariaLabel: "Einladungsumschlag oeffnen",
    };
  }

  return {
    hint: "Touchez l'enveloppe",
    ariaLabel: "Ouvrir l'enveloppe d'invitation",
  };
}

export function EnvelopeIntroOverlay() {
  const pathname = usePathname();
  const envelopeRef = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    envelopeRef.current?.focus({ preventScroll: true });

    return () => {
      document.body.classList.remove("intro-active");
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const openDuration = prefersReducedMotion ? REDUCED_OPEN_DURATION_MS : OPEN_DURATION_MS;
    const closeDuration = prefersReducedMotion ? REDUCED_CLOSE_DURATION_MS : CLOSE_DURATION_MS;
    const removeDelay = prefersReducedMotion ? REDUCED_REMOVE_DELAY_MS : REMOVE_DELAY_MS;

    const closeTimer = window.setTimeout(() => {
      document.body.classList.remove("intro-active");
      setIsClosing(true);
    }, openDuration);

    const removeTimer = window.setTimeout(() => {
      setIsVisible(false);
    }, openDuration + closeDuration + removeDelay);

    return () => {
      window.clearTimeout(closeTimer);
      window.clearTimeout(removeTimer);
    };
  }, [isOpen]);

  if (!isVisible) {
    return null;
  }

  const locale = resolveLocaleFromPath(pathname);
  const copy = getIntroCopy(locale);
  const className = [styles.overlay, isOpen ? styles.isOpen : "", isClosing ? styles.isClosing : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <div id="intro-overlay" className={className}>
      <div className={styles.scene}>
        <div className={styles.shadow} />
        <button
          ref={envelopeRef}
          type="button"
          className={styles.envelope}
          aria-label={copy.ariaLabel}
          onClick={() => {
            if (!isOpen) {
              setIsOpen(true);
            }
          }}
          disabled={isOpen}
        >
          <span className={styles.glow} aria-hidden="true" />
          <span className={styles.spark} aria-hidden="true" />
          <span className={styles.letter} aria-hidden="true">
            <span className={styles.letterInner}>
              <span className={styles.monogram}>R K</span>
              <span className={styles.crest} />
              <span className={styles.line} />
              <span className={`${styles.line} ${styles.lineShort}`} />
              <span className={styles.line} />
            </span>
          </span>
          <span className={styles.backPanel} aria-hidden="true" />
          <span className={styles.foldLeft} aria-hidden="true" />
          <span className={styles.foldRight} aria-hidden="true" />
          <span className={styles.foldBottom} aria-hidden="true" />
          <span className={styles.flap} aria-hidden="true">
            <span className={styles.seal} />
          </span>
        </button>
        <p className={styles.hint}>{copy.hint}</p>
      </div>
    </div>
  );
}
